import  { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api'; // Your axios instance
import { useAuth } from '../context/AuthContext'; // To access logout/auth state

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth(); // Only fetch if logged in

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications on load and every minute
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchNotifications = async () => {
    try {
      const response = await API.get('/notifications?limit=10');
      if (response.data.success) {
        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const toggleDropdown = () => {
    if (!isOpen) fetchNotifications(); // Refresh on open
    setIsOpen(!isOpen);
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    try {
      await API.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const handleNotificationClick = async (item) => {
    // Mark as read if not already
    if (!item.isRead) {
      try {
        await API.put(`/notifications/${item._id}/read`);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => n._id === item._id ? { ...n, isRead: true } : n));
      } catch (error) {
        console.error(error);
      }
    }
    setIsOpen(false);
    
    // Logic for redirection based on type
    if (item.type.includes('payment') || item.type.includes('subscription')) {
      navigate('/subscription');
    } else if (item.data?.url) {
      navigate(item.data.url);
    }
  };

  const handleClearOne = async (e, id) => {
    e.stopPropagation();
    try {
      await API.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n._id !== id));
      // If it was unread, decrease count
      const wasUnread = notifications.find(n => n._id === id)?.isRead === false;
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Helper to format time relative to now
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  // Helper for item styling based on type
  const getTypeClass = (type) => {
    if (type.includes('success')) return 'success';
    if (type.includes('failed') || type.includes('expired')) return 'alert';
    if (type.includes('warning') || type.includes('expiring')) return 'warning';
    return 'info';
  };

  return (
    <div className="notification-wrapper" ref={dropdownRef}>
      <style>{styles}</style>
      
      {/* 🔔 Bell Icon */}
      <button 
        className={`bell-btn ${isOpen ? 'active' : ''}`} 
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        <svg 
          width="24" height="24" viewBox="0 0 24 24" 
          fill="none" stroke="currentColor" strokeWidth="2" 
          strokeLinecap="round" strokeLinejoin="round"
          className={`bell-icon ${unreadCount > 0 ? 'shake' : ''}`}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>

        {unreadCount > 0 && (
          <span className="badge-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* 🔽 Dropdown */}
      <div className={`notification-dropdown ${isOpen ? 'show' : ''}`}>
        <div className="dropdown-header">
          <span className="header-title">Notifications</span>
          {unreadCount > 0 && (
            <button className="clear-all-btn" onClick={handleMarkAllRead}>
              Mark all read
            </button>
          )}
        </div>

        <div className="dropdown-body">
          {notifications.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">💤</div>
              <p>No new notifications</p>
            </div>
          ) : (
            <div className="notification-list">
              {notifications.map((item) => (
                <div 
                  key={item._id} 
                  className={`notification-item ${!item.isRead ? 'unread' : ''}`} 
                  onClick={() => handleNotificationClick(item)}
                >
                  <div className={`item-indicator ${getTypeClass(item.type)}`}></div>
                  <div className="item-content">
                    <div className="item-top">
                      <span className="item-title">{item.title}</span>
                      <span className="item-time">{formatTime(item.createdAt)}</span>
                    </div>
                    <p className="item-msg">{item.message}</p>
                  </div>
                  <button 
                    className="clear-one-btn" 
                    onClick={(e) => handleClearOne(e, item._id)}
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = `
  .notification-wrapper { position: relative; display: flex; align-items: center; }
  .bell-btn { background: transparent; border: none; cursor: pointer; position: relative; padding: 8px; border-radius: 50%; transition: all 0.2s ease; color: #64748b; display: flex; align-items: center; justify-content: center; }
  .bell-btn:hover, .bell-btn.active { background-color: #f1f5f9; color: #6366f1; }
  @keyframes bell-shake { 0% { transform: rotate(0); } 15% { transform: rotate(5deg); } 30% { transform: rotate(-5deg); } 45% { transform: rotate(3deg); } 60% { transform: rotate(-3deg); } 75% { transform: rotate(1deg); } 85% { transform: rotate(-1deg); } 100% { transform: rotate(0); } }
  .bell-icon.shake { animation: bell-shake 2s infinite; transform-origin: top center; }
  .badge-count { position: absolute; top: 2px; right: 2px; background: #ef4444; color: white; font-size: 10px; font-weight: 700; height: 16px; min-width: 16px; padding: 0 4px; border-radius: 10px; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.1); }
  
  .notification-dropdown { position: absolute; top: 140%; right: -10px; width: 320px; background: white; border-radius: 12px; box-shadow: 0 10px 40px -10px rgba(0,0,0,0.15); border: 1px solid #e2e8f0; opacity: 0; visibility: hidden; transform: translateY(10px); transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1); z-index: 1000; }
  .notification-dropdown::before { content: ''; position: absolute; top: -6px; right: 18px; width: 12px; height: 12px; background: white; border-left: 1px solid #e2e8f0; border-top: 1px solid #e2e8f0; transform: rotate(45deg); }
  .notification-dropdown.show { opacity: 1; visibility: visible; transform: translateY(0); }
  
  .dropdown-header { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid #f1f5f9; }
  .header-title { font-weight: 700; font-size: 14px; color: #1e293b; }
  .clear-all-btn { background: none; border: none; font-size: 11px; font-weight: 600; color: #6366f1; cursor: pointer; padding: 4px 8px; border-radius: 4px; transition: background 0.2s; }
  .clear-all-btn:hover { background: #eef2ff; }
  
  .dropdown-body { max-height: 350px; overflow-y: auto; }
  .dropdown-body::-webkit-scrollbar { width: 4px; }
  .dropdown-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
  
  .notification-list { display: flex; flex-direction: column; }
  .notification-item { padding: 12px 16px; display: flex; gap: 12px; border-bottom: 1px solid #f8fafc; cursor: pointer; transition: background 0.2s; position: relative; }
  .notification-item:hover { background: #f8fafc; }
  .notification-item.unread { background: #f0f9ff; }
  .notification-item:last-child { border-bottom: none; }
  
  .item-indicator { width: 8px; height: 8px; border-radius: 50%; margin-top: 6px; flex-shrink: 0; }
  .item-indicator.alert { background: #ef4444; box-shadow: 0 0 0 2px #fee2e2; }
  .item-indicator.warning { background: #f59e0b; box-shadow: 0 0 0 2px #fef3c7; }
  .item-indicator.info { background: #3b82f6; box-shadow: 0 0 0 2px #dbeafe; }
  .item-indicator.success { background: #22c55e; box-shadow: 0 0 0 2px #dcfce7; }
  
  .item-content { flex: 1; min-width: 0; }
  .item-top { display: flex; justify-content: space-between; margin-bottom: 2px; }
  .item-title { font-size: 13px; font-weight: 600; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px; }
  .item-time { font-size: 10px; color: #94a3b8; }
  .item-msg { font-size: 12px; color: #64748b; margin: 0; line-height: 1.4; padding-right: 15px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  
  .clear-one-btn { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: transparent; border: none; color: #cbd5e1; font-size: 18px; cursor: pointer; padding: 4px; line-height: 0.5; opacity: 0; transition: all 0.2s; }
  .notification-item:hover .clear-one-btn { opacity: 1; }
  .clear-one-btn:hover { color: #ef4444; }
  
  .empty-state { padding: 30px 20px; text-align: center; color: #94a3b8; }
  .empty-icon { font-size: 24px; margin-bottom: 8px; }
`;